import { useQueries, Query } from "@tanstack/react-query";
import axios from "axios";
import { Address } from "viem";
import { DepositToken } from "@/contexts/ContractAddressesContext";
import ENDPOINTS from "@/utils/constant/Endpoints";
import queryKeys from "@/utils/constant/QueryKeys";
import { useWalletConnection } from "@/hooks";

interface BalanceResponse {
  price?: number;
  [key: string]: unknown;
}

const fetchBalance = async (
  network: string,
  symbol: DepositToken["type"],
  address: string,
  contractAddress: Address,
  decimal?: number,
): Promise<BalanceResponse> => {
  const { data } = await axios.get<BalanceResponse>(ENDPOINTS.balance, {
    params: { network, symbol, address, contractAddress, decimal },
  });
  return data;
};

const useGetAllNetworkBalances = (
  userWalletAddress: string,
  _depositTokens: DepositToken[],
) => {
  const { address } = useWalletConnection();
  const depositTokens = _depositTokens || [];

  const queryResults = useQueries({
    queries: depositTokens.map((token) => ({
      queryKey: [queryKeys.balance, token?.type, userWalletAddress],
      queryFn: () => {
        return fetchBalance(
          token?.network || "",
          token?.type,
          userWalletAddress,
          (token?.contractAddress || "") as Address,
          token?.decimal,
        );
      },
      enabled: !!address,
      refetchInterval: (query: Query<BalanceResponse>) => {
        const data = query.state.data;
        if (!data || !data.price || data.price <= 0) {
          return 5 * 60 * 1000;
        }
        return false;
      },
    })),
  });

  return queryResults.map((result, index) => {
    return {
      type: depositTokens[index]?.type,
      accountBalance: result.data?.price,
    };
  });
};

export default useGetAllNetworkBalances;